// Fixed-timestep accumulator (the "Fix Your Timestep" pattern). Decouples a fixed-rate simulation
// from a variable display rate: the renderer calls once per animation frame, and the loop runs
// zero-or-more fixed sim steps to catch up, capping the per-frame delta to avoid a spiral of death
// after a long stall (GC, hidden tab).
//
// This owns only the timing mechanics — the consumer decides what a tick does, what a frame
// renders, and (via its own policy) when to advance vs. idle. That keeps the loop game-agnostic.

export type FixedStepConfig = {
  /** Fixed simulation step in ms. Default 1000/60 (60 Hz). */
  fixedDtMs?: number;
  /** Per-frame delta cap in ms (spiral-of-death guard). Default 250. */
  maxFrameDtMs?: number;
};

export class FixedStepLoop {
  /** The fixed step in ms (read-only; consumers may need it for sub-step interpolation). */
  readonly fixedDtMs: number;
  private readonly maxFrameDtMs: number;
  private accumulatorMs = 0;
  private lastFrameMs = 0;

  constructor(cfg?: FixedStepConfig) {
    this.fixedDtMs = cfg?.fixedDtMs ?? 1000 / 60;
    this.maxFrameDtMs = cfg?.maxFrameDtMs ?? 250;
  }

  /** Reset the accumulator. Call when (re)starting the simulation. Leaves the frame clock alone. */
  reset(): void {
    this.accumulatorMs = 0;
  }

  /**
   * Keep the frame clock current on a frame that does NOT advance the sim (menu, pause), so the
   * next active frame doesn't see the delta spike to `maxFrameDtMs`.
   */
  markFrame(nowMs: number): void {
    this.lastFrameMs = nowMs;
  }

  /** Capped elapsed time since the previous frame, advancing the frame clock. */
  nextDeltaMs(nowMs: number): number {
    if (this.lastFrameMs === 0) this.lastFrameMs = nowMs;
    let delta = nowMs - this.lastFrameMs;
    this.lastFrameMs = nowMs;
    if (delta > this.maxFrameDtMs) delta = this.maxFrameDtMs;
    return delta;
  }

  /** Feed elapsed time into the accumulator, invoking `tick` once per fixed step. */
  advance(deltaMs: number, tick: () => void): void {
    this.accumulatorMs += deltaMs;
    while (this.accumulatorMs >= this.fixedDtMs) {
      tick();
      this.accumulatorMs -= this.fixedDtMs;
    }
  }
}
